<div class="home-container bg-gray-50 min-h-screen">
  <!-- Hero Carousel Section -->
  <section class="relative bg-gray-900 overflow-hidden h-[50vh] min-h-[400px] sm:h-[60vh] sm:min-h-[500px] lg:h-[70vh]">
    <!-- Carousel Container -->
    <div *ngIf="!loading() && getCarouselProperties().length > 0" class="relative w-full h-full">
      <!-- Slides -->
      <div *ngFor="let property of getCarouselProperties(); let i = index" 
           [class.opacity-100]="currentCarouselIndex() === i"
           [class.opacity-0]="currentCarouselIndex() !== i"
           class="absolute inset-0 transition-opacity duration-700 ease-in-out">
        <!-- Background Image -->
        <div class="absolute inset-0">
          <img 
            *ngIf="propertyService.getPrimaryImage(property)?.storage_path"
            [src]="getImageUrlFromCache(propertyService.getPrimaryImage(property)!.storage_path)"
            [alt]="property.title"
            class="w-full h-full object-cover">
          <div class="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent"></div>
        </div>

        <!-- Content Overlay -->
        <div class="relative h-full flex items-center">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div class="max-w-2xl md:max-w-xl lg:max-w-2xl">
              <!-- Badges -->
              <div class="flex gap-2 mb-2 md:mb-3">
                <span class="bg-emerald-500 text-white px-2 py-1 sm:px-3 md:px-4 md:py-1.5 rounded-full text-xs sm:text-sm font-bold flex items-center gap-1 sm:gap-1.5 shadow-lg">
                  <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Featured
                </span>
                <span class="bg-white/90 backdrop-blur text-gray-900 px-2 py-1 sm:px-3 md:px-4 md:py-1.5 rounded-full text-xs sm:text-sm font-bold capitalize shadow-lg">
                  For {{ property.listing_type }}
                </span>
              </div>

              <h1 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 drop-shadow-lg leading-tight">{{ property.title }}</h1>
              
              <p class="text-sm sm:text-base md:text-lg text-gray-200 mb-2 md:mb-3 flex items-center gap-1 sm:gap-2">
                <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                </svg>
                {{ propertyService.getFullAddress(property) }}
              </p>

              <div class="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4 drop-shadow-lg">
                {{ propertyService.formatPrice(property.price, property.currency) }}
              </div>

              <!-- Property Stats -->
              <div class="flex gap-3 sm:gap-4 md:gap-5 mb-3 md:mb-5 text-white">
                <div class="flex items-center gap-1 sm:gap-2">
                  <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p class="text-base sm:text-lg md:text-xl font-bold">{{ property.bedrooms || '-' }}</p>
                    <p class="text-xs sm:text-sm text-gray-300">Bedrooms</p>
                  </div>
                </div>
                <div class="flex items-center gap-1 sm:gap-2">
                  <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p class="text-base sm:text-lg md:text-xl font-bold">{{ property.bathrooms || '-' }}</p>
                    <p class="text-xs sm:text-sm text-gray-300">Bathrooms</p>
                  </div>
                </div>
                <div class="flex items-center gap-1 sm:gap-2">
                  <div class="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                  </div>
                  <div>
                    <p class="text-base sm:text-lg md:text-xl font-bold">{{ property.area ? (property.area | number:'1.0-0') : '-' }}</p>
                    <p class="text-xs sm:text-sm text-gray-300">Area (m²)</p>
                  </div>
                </div>
              </div>

              <button 
                (click)="viewPropertyDetail(getCarouselProperties()[currentCarouselIndex()].id)"
                class="inline-flex items-center gap-1 sm:gap-2 bg-white text-immovea-navy px-4 py-2 sm:px-5 md:px-6 md:py-3 rounded-lg md:rounded-xl hover:bg-blue-50 transition font-bold text-sm sm:text-base shadow-2xl transform hover:scale-105"
                aria-label="View details for this property">
                View Property Details
                <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              
              <div class="hidden md:flex gap-3 mt-4">
                <a href="/properties" class="inline-flex items-center justify-center gap-2 bg-immovea-navy/80 backdrop-blur text-white px-5 py-2.5 rounded-lg hover:bg-immovea-navy transition font-bold text-sm shadow-xl border-2 border-white/20">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                  </svg>
                  Browse Properties
                </a>
                <a href="/quote" class="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-5 py-2.5 rounded-lg hover:bg-white hover:text-immovea-navy transition font-bold text-sm shadow-xl backdrop-blur">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  Get a Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Arrows (Hidden on mobile, visible on md+) -->
      <button 
        (click)="previousCarouselSlide()"
        aria-label="Previous property"
        class="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 hover:bg-black/60 backdrop-blur text-white rounded-full items-center justify-center transition-all z-10 shadow-lg">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
      <button 
        (click)="nextCarouselSlide()"
        aria-label="Next property"
        class="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/40 hover:bg-black/60 backdrop-blur text-white rounded-full items-center justify-center transition-all z-10 shadow-lg">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>

      <!-- Indicators -->
      <div class="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 gap-3 z-10">
        <button 
          *ngFor="let property of getCarouselProperties(); let i = index"
          (click)="goToCarouselSlide(i)"
          [attr.aria-label]="'Go to slide ' + (i + 1)"
          [attr.aria-current]="currentCarouselIndex() === i ? 'true' : 'false'"
          [class.bg-white]="currentCarouselIndex() === i"
          [class.bg-white/40]="currentCarouselIndex() !== i"
          [class.w-12]="currentCarouselIndex() === i"
          [class.w-3]="currentCarouselIndex() !== i"
          class="h-3 rounded-full transition-all duration-300 hover:bg-white/60">
        </button>
      </div>
    </div>

    <!-- Loading State for Carousel -->
    <div *ngIf="loading()" class="absolute inset-0 bg-gradient-to-r from-immovea-navy to-blue-900 flex items-center justify-center">
      <div class="text-center text-white">
        <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4"></div>
        <p class="text-xl font-semibold">Loading featured properties...</p>
      </div>
    </div>

    <!-- Fallback Hero (No Properties) -->
    <div *ngIf="!loading() && getCarouselProperties().length === 0" class="absolute inset-0 bg-gradient-to-r from-immovea-navy via-blue-800 to-blue-900 flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">Find Your Perfect Home</h1>
        <p class="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto px-2">Discover premium properties and get professional quotes. Your dream home is just a click away.</p>
        <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
          <a href="/properties" class="inline-flex items-center justify-center gap-2 bg-white text-immovea-navy px-6 py-3 sm:px-8 sm:py-4 rounded-lg hover:bg-blue-50 transition font-bold text-base sm:text-lg shadow-lg">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
            </svg>
            Browse Properties
          </a>
          <a href="/quote" class="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg hover:bg-white hover:text-immovea-navy transition font-bold text-base sm:text-lg">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            Get a Quote
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Featured Properties Section -->
  <section class="py-20 bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div class="mb-16 text-center">
        <h2 class="text-5xl font-bold text-gray-900 mb-4">Featured Properties</h2>
        <p class="text-xl text-gray-600">Handpicked listings from our premium collection</p>
      </div>

      <!-- Skeleton Loading State -->
      <div *ngIf="loading()" class="animate-pulse space-y-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div *ngFor="let i of [1,2,3]" class="bg-white rounded-lg overflow-hidden shadow-md">
            <!-- Image skeleton -->
            <div class="h-56 bg-gray-200"></div>
            <div class="p-5 space-y-3">
              <!-- Property type badge -->
              <div class="h-4 bg-gray-200 rounded w-20"></div>
              <!-- Title -->
              <div class="h-5 bg-gray-200 rounded w-3/4"></div>
              <!-- Address -->
              <div class="h-3 bg-gray-200 rounded w-full"></div>
              <!-- Price -->
              <div class="h-7 bg-gray-200 rounded w-1/2 mb-3"></div>
              <!-- Feature list -->
              <div class="space-y-2">
                <div class="h-6 bg-gray-200 rounded w-full"></div>
                <div class="h-6 bg-gray-200 rounded w-3/4"></div>
                <div class="h-6 bg-gray-200 rounded w-2/3"></div>
              </div>
              <!-- Badges -->
              <div class="flex gap-1.5 mt-3">
                <div class="h-5 bg-gray-200 rounded w-16"></div>
                <div class="h-5 bg-gray-200 rounded w-16"></div>
                <div class="h-5 bg-gray-200 rounded w-16"></div>
              </div>
              <!-- Button -->
              <div class="h-10 bg-gray-200 rounded w-full mt-3"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Properties Grid -->
      <div *ngIf="!loading() && filteredProperties.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div *ngFor="let property of filteredProperties; let propIdx = index" class="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col">
          <!-- Image Container with Carousel -->
          <div class="relative h-56 bg-gray-200 overflow-hidden group">
            <!-- Main Image -->
            <img
              *ngIf="getPropertyImageUrl(property, propIdx)"
              [src]="getPropertyImageUrl(property, propIdx)"
              [alt]="getPropertyImages(property)[propertyImageIndices[propIdx] || 0]?.alt_text || property.title"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div *ngIf="!getPropertyImageUrl(property, propIdx)" class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
              <span class="text-gray-600 font-medium">No image available</span>
            </div>
            
            <!-- Image Navigation (Multiple Images) -->
            <div *ngIf="getPropertyImages(property).length > 1" class="absolute inset-0 flex items-center justify-between px-2 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all">
              <button 
                (click)="previousPropertyImage(propIdx)"
                aria-label="View previous property image"
                class="bg-white bg-opacity-70 hover:bg-opacity-100 text-gray-900 rounded-full w-7 h-7 flex items-center justify-center transition text-lg">
                ‹
              </button>
              <button 
                (click)="nextPropertyImage(propIdx)"
                aria-label="View next property image"
                class="bg-white bg-opacity-70 hover:bg-opacity-100 text-gray-900 rounded-full w-7 h-7 flex items-center justify-center transition text-lg">
                ›
              </button>
            </div>
            
            <!-- Image Counter -->
            <div *ngIf="getPropertyImages(property).length > 1" class="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-0.5 rounded text-xs font-medium">
              {{ (propertyImageIndices[propIdx] || 0) + 1 }}/{{ getPropertyImages(property).length }}
            </div>
            
            <!-- Badges -->
            <div class="absolute top-2 right-2 flex gap-1.5">
              <div class="bg-emerald-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 shadow-md">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Featured
              </div>
              <div class="bg-immovea-navy text-white px-2 py-0.5 rounded-full text-xs font-semibold capitalize shadow-md">
                {{ property.listing_type }}
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="p-5 flex flex-col flex-grow">
            <!-- Property Type Badge -->
            <div class="inline-block px-2 py-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold rounded-md mb-2 self-start">
              {{ property.property_type }}
            </div>

            <!-- Title -->
            <h3 class="text-lg font-bold text-gray-900 mb-1.5 line-clamp-2 hover:text-immovea-navy transition">
              {{ property.title }}
            </h3>
            
            <!-- Address -->
            <p class="text-gray-600 text-xs mb-3 line-clamp-1 flex items-center gap-1">
              <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
              <span class="truncate">{{ propertyService.getFullAddress(property) }}</span>
            </p>

            <!-- Price -->
            <div class="text-2xl font-bold text-immovea-navy mb-3 pb-3 border-b border-gray-200">
              {{ propertyService.formatPrice(property.price, property.currency) }}
            </div>

            <!-- Key Features -->
            <div class="flex-grow">
            <div class="space-y-1.5 mb-3">
              <div *ngIf="property.bedrooms || property.bathrooms || property.area" class="flex items-center gap-2 text-xs text-gray-700 bg-blue-50 rounded px-2 py-1.5">
                <svg class="w-3.5 h-3.5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                <span class="font-semibold">
                  <span *ngIf="property.bedrooms">{{ property.bedrooms }} Bed<span *ngIf="property.bedrooms > 1">s</span></span>
                  <span *ngIf="property.bedrooms && property.bathrooms"> • </span>
                  <span *ngIf="property.bathrooms">{{ property.bathrooms }} Bath<span *ngIf="property.bathrooms > 1">s</span></span>
                  <span *ngIf="(property.bedrooms || property.bathrooms) && property.area"> • </span>
                  <span *ngIf="property.area">{{ property.area | number:'1.0-0' }} m²</span>
                </span>
              </div>
              <div *ngIf="property.year_built" class="flex items-center gap-2 text-xs text-gray-700 bg-slate-50 rounded px-2 py-1.5">
                <svg class="w-3.5 h-3.5 text-slate-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
                <span class="font-semibold">Built {{ property.year_built }}</span>
              </div>
              <div *ngIf="property.parking_covered || property.parking_outdoor" class="flex items-center gap-2 text-xs text-gray-700 bg-indigo-50 rounded px-2 py-1.5">
                <svg class="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
                <span class="font-semibold">{{ (property.parking_covered || 0) + (property.parking_outdoor || 0) }} Parking Spot<span *ngIf="(property.parking_covered || 0) + (property.parking_outdoor || 0) > 1">s</span></span>
              </div>
              <div *ngIf="property.is_furnished" class="flex items-center gap-2 text-xs text-gray-700 bg-amber-50 rounded px-2 py-1.5">
                <svg class="w-3.5 h-3.5 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z"/>
                </svg>
                <span class="font-semibold">Fully Furnished</span>
              </div>
            </div>

            <!-- Additional Amenities Badges -->
            <div class="flex flex-wrap gap-1.5 mb-3">
              <span *ngIf="property.has_elevator" class="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                Elevator
              </span>
              <span *ngIf="property.has_terrace" class="inline-block px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs font-semibold">
                Terrace
              </span>
              <span *ngIf="property.has_garden" class="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold">
                Garden
              </span>
              <span *ngIf="property.has_view" class="inline-block px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-semibold">
                View
              </span>
              <span *ngIf="property.has_generator" class="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                Generator
              </span>
              <span *ngIf="property.general_condition" class="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-semibold capitalize">
                {{ property.general_condition.replace('_', ' ') }}
              </span>
            </div>
            </div>

            <!-- View Button -->
            <button 
              (click)="viewPropertyDetail(property.id)"
              [attr.aria-label]="'View details for ' + property.title"
              class="w-full bg-immovea-navy text-white px-4 py-2.5 rounded-lg hover:bg-blue-900 transition font-semibold text-sm">
              View Details
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading() && filteredProperties.length === 0" class="flex items-center justify-center py-24">
        <div class="max-w-lg w-full mx-4">
          <div class="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-12 text-center border border-gray-200">
            <!-- Animated Icon -->
            <div class="relative w-28 h-28 mx-auto mb-8">
              <div class="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-2xl opacity-20 animate-pulse"></div>
              <div class="relative w-28 h-28 bg-gradient-to-br from-immovea-navy to-blue-600 rounded-2xl flex items-center justify-center shadow-xl">
                <svg class="w-14 h-14 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
            </div>
            
            <!-- Message -->
            <h3 class="text-3xl font-bold text-gray-900 mb-4">No Featured Properties</h3>
            <p class="text-lg text-gray-600 mb-8 leading-relaxed">We're currently updating our featured listings. Check back soon or browse all available properties.</p>
            
            <!-- Action Button -->
            <a href="/properties" class="inline-flex items-center gap-3 bg-gradient-to-r from-immovea-navy to-blue-700 text-white px-10 py-4 rounded-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 font-semibold text-lg">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              Explore All Properties
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA Section -->
  <section class="bg-gradient-to-r from-immovea-navy to-blue-800 text-white py-16 mt-16">
    <div class="max-w-4xl mx-auto text-center px-4">
      <h2 class="text-4xl font-bold mb-4">Ready to Find Your Next Home?</h2>
      <p class="text-xl text-blue-100 mb-8">Browse our complete listings or get a professional quote today.</p>
      <a href="/properties" class="inline-block bg-white text-immovea-navy px-8 py-4 rounded-lg hover:bg-blue-50 transition font-bold text-lg">
        Start Exploring Now
      </a>
    </div>
  </section>
</div>
